import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { X, SlidersHorizontal, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatPrice } from '@/lib/formatters';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { bodyTypeGroups, expandBodyTypeGroup, getActiveGroupKeys } from '@/lib/body-type';
import { ViewModeToggle, type CarViewMode } from '@/components/cars/ViewModeToggle';

export interface CarFilters {
  modelId: string | null;
  query: string;
  maxPrice: number | null;
  powertrain: string[];
  bodyType: string[];
  drivetrain: string | null;
  minRange: number | null;
  minTrunk: number | null;
  minTowing: number | null;
  minSeats: number | null;
  includeOptionalSeats: boolean;
  minPower: number | null;
  minChargingDcKw: number | null;
  maxAcceleration: number | null;
  brandId: string | null;
  showUpcoming: 'all' | 'only' | 'exclude' | null;
  minIsofix: number | null;
  threeIsofixSecondRow: 'any' | 'yes' | 'no';
}

interface CarSearchFiltersProps {
  filters: CarFilters;
  onFiltersChange: (filters: CarFilters) => void;
  brands: Pick<Tables<'brands'>, 'id' | 'name'>[];
  models?: Array<Pick<Tables<'models'>, 'id' | 'name'> & { brand_id?: string | null; brands?: { name: string } | null }>;
  onReset: () => void;
  viewMode?: CarViewMode;
  onViewModeChange?: (mode: CarViewMode) => void;
}

export function CarSearchFilters({ filters, onFiltersChange, brands, models = [], onReset, viewMode, onViewModeChange }: CarSearchFiltersProps) {



  const updateFilter = <K extends keyof CarFilters>(key: K, value: CarFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleArrayFilter = (key: 'bodyType', value: string) => {
    const current = filters[key];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateFilter(key, updated);
  };

  const hasActiveFilters = filters.query || filters.maxPrice || 
    filters.bodyType.length > 0 || filters.minRange || filters.minTrunk || 
    filters.minTowing || filters.minSeats || filters.minPower || filters.drivetrain || 
    filters.minChargingDcKw || filters.maxAcceleration || filters.brandId || filters.modelId || filters.showUpcoming ||
    filters.minIsofix || filters.threeIsofixSecondRow !== 'any';

  return (
    <div className="bg-card rounded-xl shadow-card p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">Filtre</h2>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onReset} className="text-muted-foreground">
            <X className="w-4 h-4 mr-1" />
            Nulstil
          </Button>
        )}
      </div>

      {filters.query && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <div className="text-xs text-foreground min-w-0">
            <span className="text-muted-foreground">Søgning: </span>
            <span className="font-medium truncate">«{filters.query}»</span>
          </div>
          <button
            type="button"
            onClick={() => updateFilter('query', '')}
            className="flex-shrink-0 rounded-full p-1 hover:bg-primary/10 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Ryd søgning"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <Link
        to="/find-bil"
        className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-950/30 px-3 py-2 text-xs font-medium text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Brug elbilhjælperen i stedet
      </Link>

      {viewMode && onViewModeChange && (
        <div className="space-y-2 pt-1">
          <p className="text-xs font-medium text-muted-foreground">Visning</p>
          <ViewModeToggle value={viewMode} onChange={onViewModeChange} className="w-full justify-start" />
        </div>
      )}



      <Accordion type="multiple" defaultValue={['availability', 'brand', 'powertrain', 'price']} className="w-full">
        {/* Availability Filter */}
        <AccordionItem value="availability">
          <AccordionTrigger className="text-sm font-medium">Tilgængelighed</AccordionTrigger>
          <AccordionContent>
            <Select
              value={filters.showUpcoming || 'all'}
              onValueChange={(value) => updateFilter('showUpcoming', value as 'all' | 'only' | 'exclude')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle biler</SelectItem>
                <SelectItem value="exclude">Kun biler på markedet</SelectItem>
                <SelectItem value="only">Kun kommende biler</SelectItem>
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>


        {/* Brand Filter */}
        <AccordionItem value="brand">
          <AccordionTrigger className="text-sm font-medium">Mærke</AccordionTrigger>
          <AccordionContent>
            <Select 
              value={filters.brandId || 'all'} 
              onValueChange={(value) => {
                const newBrandId = value === 'all' ? null : value;
                onFiltersChange({ ...filters, brandId: newBrandId, modelId: null });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Alle mærker" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle mærker</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {models.length > 0 && (
              <div className="mt-3">
                <Label className="text-sm font-medium mb-1.5 block">Model</Label>
                <Select
                  value={filters.modelId || 'all'}
                  onValueChange={(value) => {
                    if (value === 'all') {
                      updateFilter('modelId', null);
                      return;
                    }
                    const selected = models.find((m) => m.id === value);
                    // Hvis intet mærke er valgt, sæt mærket automatisk fra den valgte model
                    if (!filters.brandId && selected?.brand_id) {
                      onFiltersChange({ ...filters, brandId: selected.brand_id, modelId: value });
                    } else {
                      updateFilter('modelId', value);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alle modeller" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle modeller</SelectItem>
                    {[...models]
                      .sort((a, b) => {
                        if (!filters.brandId) {
                          const ba = a.brands?.name || '';
                          const bb = b.brands?.name || '';
                          if (ba !== bb) return ba.localeCompare(bb, 'da');
                        }
                        return a.name.localeCompare(b.name, 'da');
                      })
                      .map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {!filters.brandId && model.brands?.name
                            ? `${model.brands.name} ${model.name}`
                            : model.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          </AccordionContent>
        </AccordionItem>

        {/* Price Filter */}
        <AccordionItem value="price">
          <AccordionTrigger className="text-sm font-medium">Maksimumpris</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <Slider
                value={[filters.maxPrice || 2000000]}
                min={100000}
                max={2000000}
                step={50000}
                onValueChange={([value]) => updateFilter('maxPrice', value === 2000000 ? null : value)}
              />
              <div className="text-sm text-muted-foreground text-center">
                {filters.maxPrice ? formatPrice(filters.maxPrice) : 'Ingen grænse'}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>




        {/* Body Type Filter */}
        <AccordionItem value="bodyType">
          <AccordionTrigger className="text-sm font-medium">Karosseri</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {(() => {
                const activeGroups = getActiveGroupKeys(filters.bodyType);
                return bodyTypeGroups.map((group) => {
                  const checked = activeGroups.has(group.key);
                  const onToggle = () => {
                    const members = expandBodyTypeGroup(group.key);
                    const next = checked
                      ? filters.bodyType.filter((v) => !members.includes(v as any))
                      : Array.from(new Set([...filters.bodyType, ...members]));
                    updateFilter('bodyType', next);
                  };
                  return (
                    <div key={group.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`body-${group.key}`}
                        checked={checked}
                        onCheckedChange={onToggle}
                      />
                      <Label htmlFor={`body-${group.key}`} className="text-sm cursor-pointer">
                        {group.label}
                      </Label>
                    </div>
                  );
                });
              })()}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* EV specific filters */}
        <AccordionItem value="ev-specs">
          <AccordionTrigger className="text-sm font-medium">Batteri & Opladning</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="minRange" className="text-sm">Min. WLTP rækkevidde (km)</Label>
                <Input
                  id="minRange"
                  type="number"
                  placeholder="f.eks. 300"
                  value={filters.minRange || ''}
                  onChange={(e) => updateFilter('minRange', e.target.value ? parseInt(e.target.value) : null)}
                />
                {filters.minRange ? (
                  <p className="text-xs text-muted-foreground">Rækkevidde gælder kun elbiler – drivlinje sættes automatisk til El.</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Min. DC-ladning (kW)</Label>
                <Select
                  value={filters.minChargingDcKw?.toString() || 'all'}
                  onValueChange={(value) => updateFilter('minChargingDcKw', value === 'all' ? null : parseInt(value))}
                >
                  <SelectTrigger><SelectValue placeholder="Alle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="100">100+ kW</SelectItem>
                    <SelectItem value="150">150+ kW</SelectItem>
                    <SelectItem value="200">200+ kW</SelectItem>
                    <SelectItem value="250">250+ kW</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>



        {/* Practical specs */}
        <AccordionItem value="practical">
          <AccordionTrigger className="text-sm font-medium">Praktisk</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="minSeats" className="text-sm">Min. antal sæder</Label>
                <Select 
                  value={filters.minSeats?.toString() || 'all'} 
                  onValueChange={(value) => updateFilter('minSeats', value === 'all' ? null : parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="5">5+ sæder</SelectItem>
                    <SelectItem value="6">6+ sæder</SelectItem>
                    <SelectItem value="7">7+ sæder</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Inkl. elbiler med tilkøbsmulighed</p>
                {filters.minSeats && filters.minSeats >= 6 && (
                  <div className="flex items-start gap-2 pt-1">
                    <Checkbox
                      id="includeOptionalSeats"
                      checked={filters.includeOptionalSeats}
                      onCheckedChange={(checked) => updateFilter('includeOptionalSeats', checked !== false)}
                    />
                    <Label htmlFor="includeOptionalSeats" className="text-xs font-normal leading-snug cursor-pointer">
                      Medtag biler hvor 6/7 sæder er et tilvalg (ikke standard)
                    </Label>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="minTrunk" className="text-sm">Min. bagagerum (L)</Label>
                <Input
                  id="minTrunk"
                  type="number"
                  placeholder="f.eks. 400"
                  value={filters.minTrunk || ''}
                  onChange={(e) => updateFilter('minTrunk', e.target.value ? parseInt(e.target.value) : null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="minTowing" className="text-sm">Min. trækvægt (kg)</Label>
                <Input
                  id="minTowing"
                  type="number"
                  placeholder="f.eks. 1500"
                  value={filters.minTowing || ''}
                  onChange={(e) => updateFilter('minTowing', e.target.value ? parseInt(e.target.value) : null)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Min. antal Isofix</Label>
                <Select
                  value={filters.minIsofix?.toString() || 'all'}
                  onValueChange={(value) => updateFilter('minIsofix', value === 'all' ? null : parseInt(value))}
                >
                  <SelectTrigger><SelectValue placeholder="Alle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">3 Isofix på 2. sæderække</Label>
                <Select
                  value={filters.threeIsofixSecondRow}
                  onValueChange={(value) => updateFilter('threeIsofixSecondRow', value as 'any' | 'yes' | 'no')}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Ligegyldigt</SelectItem>
                    <SelectItem value="yes">Ja</SelectItem>
                    <SelectItem value="no">Nej</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
